import { useState, useMemo } from "react";
// Porcentajes de utilidades según rubro (D. Leg. 892)
const RUBROS = [
  { id: "pesquera", nombre: "Pesquera", tasa: 0.10 },
  { id: "telecom", nombre: "Telecomunicaciones", tasa: 0.10 },
  { id: "industrial", nombre: "Industrial", tasa: 0.10 },
  { id: "minera", nombre: "Minera", tasa: 0.08 },
  { id: "comercio", nombre: "Comercio y restaurantes", tasa: 0.08 },
  { id: "otras", nombre: "Otras actividades", tasa: 0.05 },
];

function formatearSoles(valor) {
  return valor.toLocaleString("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function CalculadoraUtilidades() {
  const [rubroId, setRubroId] = useState("comercio");
  const [utilidadEmpresa, setUtilidadEmpresa] = useState(500000);

  const [sueldoMensual, setSueldoMensual] = useState(3000);
  const [mesesTrabajados, setMesesTrabajados] = useState(12);
  const [diasTrabajadorUsuario, setDiasTrabajadorUsuario] = useState(300);

  const [diasTotalesEmpresa, setDiasTotalesEmpresa] = useState(15000);
  const [planillaAnualEmpresa, setPlanillaAnualEmpresa] = useState(1800000);

  const rubro = RUBROS.find((r) => r.id === rubroId) ?? RUBROS[0];

  const resultado = useMemo(() => {
    const utilidad = Math.max(0, Number(utilidadEmpresa) || 0);
    const totalDistribuible = utilidad * rubro.tasa;
    const mitadDias = totalDistribuible * 0.5;
    const mitadRemuneracion = totalDistribuible * 0.5;

    const remuneracionAnualUsuario = (Number(sueldoMensual) || 0) * (Number(mesesTrabajados) || 0);
    const diasUsuario = Math.max(0, Number(diasTrabajadorUsuario) || 0);
    const diasEmpresa = Math.max(1, Number(diasTotalesEmpresa) || 1);
    const planillaEmpresa = Math.max(1, Number(planillaAnualEmpresa) || 1);

    const participacionPorDias = mitadDias * (diasUsuario / diasEmpresa);
    const participacionPorRemuneracion =
      mitadRemuneracion * (remuneracionAnualUsuario / planillaEmpresa);

    const totalSinTope = participacionPorDias + participacionPorRemuneracion;
    const tope = (Number(sueldoMensual) || 0) * 18;
    const totalFinal = Math.min(totalSinTope, tope);
    const seAplicoTope = totalSinTope > tope;

    return {
      totalDistribuible,
      mitadDias,
      mitadRemuneracion,
      participacionPorDias,
      participacionPorRemuneracion,
      totalSinTope,
      tope,
      totalFinal,
      seAplicoTope,
    };
  }, [
    utilidadEmpresa,
    rubro,
    sueldoMensual,
    mesesTrabajados,
    diasTrabajadorUsuario,
    diasTotalesEmpresa,
    planillaAnualEmpresa,
  ]);

  const inputClase =
    "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700";

  return (
    <div className="w-full min-h-full px-4 py-10 bg-stone-50">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center text-lg text-white bg-red-700 rounded-full h-11 w-11">
            📈
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">
              Calculadora de Utilidades
            </h1>
            <p className="text-sm text-slate-500">Perú 2026 · Participación en las utilidades (D. Leg. 892)</p>
          </div>
        </div>

        <div className="overflow-hidden bg-white border shadow-sm rounded-2xl border-slate-200">
          <div className="p-6 space-y-5 border-b border-slate-100">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Rubro de la empresa
              </label>
              <select
                value={rubroId}
                onChange={(e) => setRubroId(e.target.value)}
                className={inputClase}
              >
                {RUBROS.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nombre} — {(r.tasa * 100).toFixed(0)}%
                  </option>
                ))}
              </select>
              <p className="mt-1.5 text-xs text-slate-400">
                Solo aplica a empresas del régimen general con más de 20 trabajadores.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Utilidad de la empresa antes de impuestos (anual)
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                  S/
                </span>
                <input
                  type="number"
                  min="0"
                  value={utilidadEmpresa}
                  onChange={(e) => setUtilidadEmpresa(e.target.value)}
                  className={`${inputClase} pl-9`}
                />
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <p className="mb-3 text-xs font-semibold tracking-wide uppercase text-slate-500">
                Tus datos
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Sueldo mensual
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                      S/
                    </span>
                    <input
                      type="number"
                      min="0"
                      value={sueldoMensual}
                      onChange={(e) => setSueldoMensual(e.target.value)}
                      className={`${inputClase} pl-9`}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Meses trabajados en el año
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="12"
                    value={mesesTrabajados}
                    onChange={(e) => setMesesTrabajados(e.target.value)}
                    className={inputClase}
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Días trabajados en el año
                </label>
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={diasTrabajadorUsuario}
                  onChange={(e) => setDiasTrabajadorUsuario(e.target.value)}
                  className={inputClase}
                />
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <p className="mb-3 text-xs font-semibold tracking-wide uppercase text-slate-500">
                Datos de toda la empresa
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Suma de días trabajados por todo el personal (año)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={diasTotalesEmpresa}
                    onChange={(e) => setDiasTotalesEmpresa(e.target.value)}
                    className={inputClase}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Planilla anual total de la empresa
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                      S/
                    </span>
                    <input
                      type="number"
                      min="1"
                      value={planillaAnualEmpresa}
                      onChange={(e) => setPlanillaAnualEmpresa(e.target.value)}
                      className={`${inputClase} pl-9`}
                    />
                  </div>
                  <p className="mt-1 text-xs text-slate-400">
                    Estos dos datos suelen estar en tu constancia de utilidades o los
                    puede brindar RR.HH.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 bg-slate-900">
            <p className="mb-1 text-xs tracking-wide uppercase text-slate-400">
              Utilidades estimadas a recibir
            </p>
            <p className="mb-5 font-mono text-4xl font-bold text-white">
              {formatearSoles(Math.max(0, resultado.totalFinal))}
            </p>

            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-300">
                <span>Monto distribuible ({(rubro.tasa * 100).toFixed(0)}% de la utilidad)</span>
                <span className="font-mono">{formatearSoles(resultado.totalDistribuible)}</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>Por días laborados (50%)</span>
                <span className="font-mono">{formatearSoles(resultado.participacionPorDias)}</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>Por remuneración percibida (50%)</span>
                <span className="font-mono">{formatearSoles(resultado.participacionPorRemuneracion)}</span>
              </div>
              {resultado.seAplicoTope && (
                <div className="flex justify-between text-amber-400 border-t border-slate-700 pt-1.5 mt-1.5">
                  <span>Tope aplicado (18 remuneraciones)</span>
                  <span className="font-mono">{formatearSoles(resultado.tope)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <p className="mt-4 text-xs leading-relaxed text-slate-400">
          Herramienta orientativa. La participación individual tiene un tope legal de
          18 remuneraciones mensuales del trabajador; el excedente pasa a un fondo del
          Ministerio de Trabajo. Solo trabajadores con vínculo laboral vigente y de
          empresas de más de 20 trabajadores tienen derecho a este beneficio. No
          constituye asesoría legal, contable ni laboral.
        </p>
      </div>
    </div>
  );
}